import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  FormControlLabel,
  Switch,
  Tooltip,
} from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { useAuth } from '../../contexts/AuthContext';
import RawDataTab from './eventTabs/RawDataTab';
import TemperatureChartTab from './eventTabs/TemperatureChartTab';
import ClassDetectionTab from './eventTabs/ClassDetectionTab';
import RelayControlTab from './eventTabs/RelayControlTab';
import BatteryChartTab from './eventTabs/BatteryChartTab';
import SnapshotTab from './eventTabs/SnapshotTab';
import DetectionTab from './eventTabs/DetectionTab';
import PlateSightingsTab from './eventTabs/PlateSightingsTab';
import ContainerEventsTab from './eventTabs/ContainerEventsTab';
import { Device } from './types';

type Props = { deviceId: string };

const RELAY_DEVICE_TYPE_ID = '111e4567-e89b-12d3-a456-426614174000';

const DeviceEventsComponent: React.FC<Props> = ({ deviceId }) => {
  const auth = useAuth();
  const accessToken = auth.tokens?.accessToken;
  const [tabValue, setTabValue] = useState(0);
  const [device, setDevice] = useState<Device | null>(null);
  const [loading, setLoading] = useState(true);
  const [includeChildren, setIncludeChildren] = useState(true);
  
  const hasRelayRole = React.useMemo(
    () => auth.hasRole('DMO_ADMIN') || auth.hasRole('DMO_RELAY'),
    [auth]
  );

  useEffect(() => {
    const fetchDevice = async () => {
      try {
        const res = await fetch(`/api/devices/${deviceId}?includeChildren=true`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (res.ok) {
          const deviceData = await res.json();
          setDevice(deviceData);
        }
      } catch (err) {
        console.error('Failed to fetch device', err);
      } finally {
        setLoading(false);
      }
    };
    fetchDevice();
  }, [deviceId, accessToken]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const isRelayDevice = React.useMemo(() => {
    if (!device) return false;
    if (device.deviceTypeId === RELAY_DEVICE_TYPE_ID) return true;
    const typeName = device.deviceType?.name?.toLowerCase() || '';
    if (typeName.includes('relay')) return true;
    return false;
  }, [device]);
  const showRelayControl = isRelayDevice && hasRelayRole;
  const isNprDevice = device?.inferenceTask === 'npr';
  const isContainerDevice = device?.inferenceTask === 'container';
  const hasChildren = (device?.childDevices?.length ?? 0) > 0;

  // Build ordered tab list dynamically to avoid index arithmetic everywhere
  const tabs = React.useMemo(() => {
    const list: { label: string; component: React.ReactNode }[] = [];
    if (showRelayControl) {
      list.push({ label: 'Relay Control', component: <RelayControlTab deviceId={deviceId} /> });
    }
    // Number-plate sites/cameras: their sightings come first, as that's what they're for
    if (isNprDevice) {
      list.push({ label: 'Number Plates', component: <PlateSightingsTab deviceId={deviceId} includeChildren={includeChildren} /> });
    }
    // ...and container docks/cameras, theirs
    if (isContainerDevice) {
      list.push({ label: 'Container Location', component: <ContainerEventsTab deviceId={deviceId} includeChildren={includeChildren} /> });
    }
    list.push({ label: 'Snapshots',         component: <SnapshotTab deviceId={deviceId} includeChildren={includeChildren} /> });
    list.push({ label: 'Detections',         component: <DetectionTab deviceId={deviceId} includeChildren={includeChildren} /> });
    list.push({ label: 'Raw Data',          component: <RawDataTab deviceId={deviceId} includeChildren={includeChildren} /> });
    // Number-plate and container cameras don't report temperature/battery
    if (!isNprDevice && !isContainerDevice) {
      list.push({ label: 'Temperature Chart', component: <TemperatureChartTab deviceId={deviceId} includeChildren={includeChildren} /> });
      list.push({ label: 'Battery Chart',     component: <BatteryChartTab deviceId={deviceId} includeChildren={includeChildren} deviceSettings={device?.deviceSettings} /> });
    }
    // A plate site logs passes, not detections; a container site logs both, so its classes can be charted
    if (!isNprDevice) {
      list.push({ label: 'Class Detection',   component: <ClassDetectionTab deviceId={deviceId} includeChildren={includeChildren} /> });
    }
    return list;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showRelayControl, isNprDevice, isContainerDevice, deviceId, includeChildren]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">Device Events</Typography>
        {hasChildren && (
          <Tooltip title="Include data from child devices (e.g. cameras linked to this device)">
            <FormControlLabel
              control={
                <Switch
                  checked={includeChildren}
                  onChange={(e) => {
                    setIncludeChildren(e.target.checked);
                    setTabValue(0);
                  }}
                  color="primary"
                  size="small"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AccountTreeIcon sx={{ fontSize: 16, color: includeChildren ? 'primary.main' : 'text.secondary' }} />
                  <Typography variant="body2" sx={{ color: includeChildren ? 'primary.main' : 'text.secondary', fontWeight: includeChildren ? 600 : 400 }}>
                    Include Children
                  </Typography>
                </Box>
              }
              sx={{ mr: 0 }}
            />
          </Tooltip>
        )}
      </Box>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          aria-label="event tabs"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          {tabs.map((tab, i) => (
            <Tab key={tab.label} label={tab.label} id={`event-tab-${i}`} aria-controls={`event-tabpanel-${i}`} />
          ))}
        </Tabs>
      </Box>

      {tabs.map((tab, i) => (
        <div
          key={tab.label}
          role="tabpanel"
          hidden={tabValue !== i}
          id={`event-tabpanel-${i}`}
          aria-labelledby={`event-tab-${i}`}
        >
          {tabValue === i && <Box sx={{ py: 3 }}>{tab.component}</Box>}
        </div>
      ))}
    </Box>
  );
};

export default DeviceEventsComponent;
